import React, { useState, useEffect, useRef, useId } from 'react';
import { addClient } from './api';
import AddressAutocomplete from './AddressAutocomplete';
import { formatPhone } from '../format';
import { useTranslation } from '../i18n';

const addClientCSS = `
  @media (max-width: 720px) {
    .ac-grid { grid-template-columns: 1fr !important; }
  }
  .ms-add-client-savebtn:hover:not(:disabled) { background: #0f4d2c !important; }
`;

export default function AddClient({ onClientAdded, onCancel }) {
  const { t } = useTranslation();
  const DAYS = [
    t('add_client.day_sunday'),
    t('add_client.day_monday'),
    t('add_client.day_tuesday'),
    t('add_client.day_wednesday'),
    t('add_client.day_thursday'),
    t('add_client.day_friday'),
    t('add_client.day_saturday'),
  ];

  const titleId = useId();
  const errorId = useId();
  const nameId = useId();
  const phoneId = useId();
  const streetId = useId();
  const streetHintId = useId();
  const cityId = useId();
  const notesId = useId();
  const shiftId = useId();
  const shiftHintId = useId();
  const visitDateId = useId();
  const freqId = useId();
  const intervalId = useId();
  const dowId = useId();
  const domId = useId();
  const nextDateId = useId();
  const amountId = useId();
  const recurNotesId = useId();

  const [form, setForm] = useState({
    name: '', phone: '', street: '', city: '', notes: '',
    visit_type: 'recurring',
    recurring_frequency: 'weekly',
    recurring_interval: 1,
    recurring_day_of_week: '',
    recurring_day_of_month: '',
    recurring_amount: '',
    recurring_notes: '',
    next_visit_date: '',
    preferred_shift: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const modalRef = useRef(null);
  const firstFieldRef = useRef(null);
  const triggerRef = useRef(null);
  const mountedRef = useRef(true);

  // Focus management: capture the previously-focused element, focus the first input on mount,
  // restore focus on unmount.
  useEffect(() => {
    triggerRef.current = document.activeElement;
    if (firstFieldRef.current && typeof firstFieldRef.current.focus === 'function') {
      firstFieldRef.current.focus();
    }
    return () => {
      mountedRef.current = false;
      const trigger = triggerRef.current;
      if (trigger && typeof trigger.focus === 'function') {
        try { trigger.focus(); } catch (e) { /* ignore */ }
      }
    };
  }, []);

  // Escape closes the modal.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (onCancel) onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onCancel) onCancel();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.street || !form.city) {
      setError(t('add_client.err_required_fields'));
      return;
    }
    setLoading(true);
    try {
      const { data } = await addClient({
        name: form.name,
        phone: form.phone,
        street: form.street,
        city: form.city,
        notes: form.notes,
        visit_type: form.visit_type,
        recurring_enabled: form.visit_type === 'recurring',
        recurring_frequency: form.visit_type === 'recurring' ? form.recurring_frequency : null,
        recurring_interval: parseInt(form.recurring_interval) || 1,
        recurring_day_of_week: form.recurring_day_of_week !== '' ? parseInt(form.recurring_day_of_week) : null,
        recurring_day_of_month: form.recurring_day_of_month ? parseInt(form.recurring_day_of_month) : null,
        recurring_amount: parseFloat(form.recurring_amount) || 0,
        recurring_notes: form.recurring_notes,
        next_visit_date: form.next_visit_date || null,
        preferred_shift: form.preferred_shift || null
      });
      onClientAdded(data);
    } catch (err) {
      if (mountedRef.current) setError(t('add_client.err_save'));
    }
    if (mountedRef.current) setLoading(false);
  };

  const requiredMark = (
    <>
      {' '}
      <span aria-hidden="true">*</span>
      <span className="ms-sr-only"> ({t('a11y.required')})</span>
    </>
  );

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div
        ref={modalRef}
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <style>{addClientCSS}</style>
        <div style={styles.titleRow}>
          <h2 id={titleId} style={styles.title}>{t('add_client.title')}</h2>
          <button
            type="button"
            style={styles.closeBtn}
            onClick={onCancel}
            aria-label={t('a11y.close_dialog')}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
        {error && (
          <p id={errorId} style={styles.error} role="alert">{error}</p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="ac-grid" style={styles.grid}>
          <div style={styles.col}>
          <div style={styles.section}>{t('add_client.section_client_info')}</div>
          <div style={styles.field}>
            <label htmlFor={nameId} style={styles.label}>
              {t('add_client.full_name')}{requiredMark}
            </label>
            <input
              ref={firstFieldRef}
              id={nameId}
              style={styles.input}
              name="name"
              placeholder={t('add_client.full_name_placeholder')}
              value={form.name}
              onChange={handleChange}
              aria-required="true"
              aria-describedby={error ? errorId : undefined}
            />
          </div>
          <div style={styles.field}>
            <label htmlFor={phoneId} style={styles.label}>{t('add_client.phone')}</label>
            <input
              id={phoneId}
              style={styles.input}
              name="phone"
              inputMode="tel"
              placeholder={t('add_client.phone_placeholder')}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
            />
          </div>
          <div style={styles.field}>
            <label htmlFor={streetId} style={styles.label}>
              {t('add_client.street')}{requiredMark}
            </label>
            <AddressAutocomplete
              id={streetId}
              style={styles.input}
              placeholder={t('add_client.street_placeholder')}
              value={form.street}
              onStreetChange={(v) => setForm({ ...form, street: v })}
              onSelect={({ street, city }) => setForm({ ...form, street, city: city || form.city })}
              aria-required="true"
              aria-describedby={streetHintId}
              aria-label={t('add_client.street')}
            />
            <div id={streetHintId} style={styles.hint}>{t('add_client.street_hint')}</div>
          </div>
          <div style={styles.field}>
            <label htmlFor={cityId} style={styles.label}>
              {t('add_client.city')}{requiredMark}
            </label>
            <input
              id={cityId}
              style={styles.input}
              name="city"
              placeholder={t('add_client.city_placeholder')}
              value={form.city}
              onChange={handleChange}
              aria-required="true"
            />
          </div>
          <div style={styles.field}>
            <label htmlFor={notesId} style={styles.label}>{t('add_client.notes')}</label>
            <input
              id={notesId}
              style={styles.input}
              name="notes"
              placeholder={t('add_client.notes_placeholder')}
              value={form.notes}
              onChange={handleChange}
            />
          </div>

          </div>
          <div style={styles.col}>
          <div style={styles.section}>{t('add_client.section_visit_schedule')}</div>
          <div style={styles.field}>
            <div style={styles.label} id={`${titleId}-vtype`}>{t('add_client.visit_type')}</div>
            <div style={styles.typeRow} role="radiogroup" aria-labelledby={`${titleId}-vtype`}>
              {[
                { value: 'recurring', label: t('add_client.type_recurring'), emoji: '🔄' },
                { value: 'one_time', label: t('add_client.type_one_time'), emoji: '1️⃣' },
                { value: 'none', label: t('add_client.type_none'), emoji: '⛔' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={form.visit_type === opt.value}
                  style={{
                    ...styles.typeBtn,
                    ...(form.visit_type === opt.value ? styles.typeBtnActive : {})
                  }}
                  onClick={() => setForm({ ...form, visit_type: opt.value })}
                >
                  <span aria-hidden="true">{opt.emoji}</span> {opt.label}
                </button>
              ))}
            </div>
          </div>

          {form.visit_type !== 'none' && (
            <div style={styles.field}>
              <label htmlFor={shiftId} style={styles.label}>{t('add_client.preferred_shift')}</label>
              <select
                id={shiftId}
                style={styles.input}
                name="preferred_shift"
                value={form.preferred_shift}
                onChange={handleChange}
                aria-describedby={shiftHintId}
              >
                <option value="">{t('add_client.no_preference')}</option>
                <option value="morning">☀️ {t('add_client.shift_morning')}</option>
                <option value="afternoon">🌤️ {t('add_client.shift_afternoon')}</option>
                <option value="evening">🌙 {t('add_client.shift_evening')}</option>
              </select>
              <div id={shiftHintId} style={styles.hint}>{t('add_client.preferred_shift_hint')}</div>
            </div>
          )}

          {form.visit_type === 'one_time' && (
            <div style={styles.field}>
              <label htmlFor={visitDateId} style={styles.label}>{t('add_client.visit_date')}</label>
              <input
                id={visitDateId}
                type="date"
                style={styles.input}
                name="next_visit_date"
                value={form.next_visit_date}
                onChange={handleChange}
              />
            </div>
          )}

          {form.visit_type === 'recurring' && (
            <>
              <div style={styles.field}>
                <label htmlFor={freqId} style={styles.label}>{t('add_client.frequency')}</label>
                <select
                  id={freqId}
                  style={styles.input}
                  name="recurring_frequency"
                  value={form.recurring_frequency}
                  onChange={handleChange}
                >
                  <option value="weekly">{t('add_client.freq_weekly')}</option>
                  <option value="biweekly">{t('add_client.freq_biweekly')}</option>
                  <option value="custom_weeks">{t('add_client.freq_custom_weeks')}</option>
                  <option value="monthly">{t('add_client.freq_monthly')}</option>
                </select>
              </div>
              {form.recurring_frequency === 'custom_weeks' && (
                <div style={styles.field}>
                  <label htmlFor={intervalId} style={styles.label}>{t('add_client.every_how_many_weeks')}</label>
                  <input
                    id={intervalId}
                    type="number"
                    style={styles.input}
                    name="recurring_interval"
                    min="1"
                    max="12"
                    value={form.recurring_interval}
                    onChange={handleChange}
                  />
                </div>
              )}
              {(form.recurring_frequency === 'weekly' || form.recurring_frequency === 'biweekly' || form.recurring_frequency === 'custom_weeks') && (
                <div style={styles.field}>
                  <label htmlFor={dowId} style={styles.label}>{t('add_client.preferred_day')}</label>
                  <select
                    id={dowId}
                    style={styles.input}
                    name="recurring_day_of_week"
                    value={form.recurring_day_of_week}
                    onChange={handleChange}
                  >
                    <option value="">{t('add_client.any_day')}</option>
                    {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
              )}
              {form.recurring_frequency === 'monthly' && (
                <div style={styles.field}>
                  <label htmlFor={domId} style={styles.label}>{t('add_client.day_of_month')}</label>
                  <input
                    id={domId}
                    type="number"
                    style={styles.input}
                    name="recurring_day_of_month"
                    min="1"
                    max="31"
                    placeholder={t('add_client.day_of_month_placeholder')}
                    value={form.recurring_day_of_month}
                    onChange={handleChange}
                  />
                </div>
              )}
              <div style={styles.field}>
                <label htmlFor={nextDateId} style={styles.label}>{t('add_client.next_visit_date_optional')}</label>
                <input
                  id={nextDateId}
                  type="date"
                  style={styles.input}
                  name="next_visit_date"
                  value={form.next_visit_date}
                  onChange={handleChange}
                />
              </div>
            </>
          )}

          <div style={styles.field}>
            <label htmlFor={amountId} style={styles.label}>{t('add_client.amount_per_visit')}</label>
            <input
              id={amountId}
              type="number"
              style={styles.input}
              name="recurring_amount"
              placeholder="0.00"
              value={form.recurring_amount}
              onChange={handleChange}
            />
          </div>
          <div style={styles.field}>
            <label htmlFor={recurNotesId} style={styles.label}>{t('add_client.schedule_notes_optional')}</label>
            <input
              id={recurNotesId}
              type="text"
              style={styles.input}
              name="recurring_notes"
              placeholder={t('add_client.schedule_notes_placeholder')}
              value={form.recurring_notes}
              onChange={handleChange}
            />
          </div>

          </div>
          </div>
          <div style={styles.actions}>
            <button type="button" style={styles.cancelBtn} onClick={onCancel}>
              {t('add_client.cancel')}
            </button>
            <button
              type="submit"
              className="ms-add-client-savebtn"
              style={styles.saveBtn}
              disabled={loading}
            >
              {loading ? t('add_client.saving') : t('add_client.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 24, width: '100%', maxWidth: 720, margin: '0 16px', maxHeight: '90vh', overflowY: 'auto' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' },
  col: { minWidth: 0 },
  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { fontSize: 18, fontWeight: 500, margin: 0, color: '#1a1a1a' },
  closeBtn: { border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#5f6b58', padding: 4, lineHeight: 1, borderRadius: 6 },
  section: { fontSize: 11, fontWeight: 500, color: '#5f6b58', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, marginTop: 16, paddingBottom: 6, borderBottom: '1px solid #f0f0f0' },
  error: { color: '#dc2626', fontSize: 13, marginBottom: 12 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 500, color: '#5f6b58', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, color: '#1a1a1a', boxSizing: 'border-box' },
  hint: { fontSize: 11, color: '#5f6b58', marginTop: 3 },
  typeRow: { display: 'flex', gap: 6 },
  typeBtn: { flex: 1, padding: '8px 6px', border: '1px solid #ddd', background: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer', color: '#5f6b58' },
  typeBtnActive: { background: '#EAF3DE', borderColor: '#14653a', color: '#14653a', fontWeight: 500 },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 },
  cancelBtn: { padding: '8px 16px', border: '1px solid #ddd', background: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#1a1a1a' },
  saveBtn: { padding: '8px 18px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 13, fontWeight: 500, color: '#fff', cursor: 'pointer' }
};
